import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Check, X, MapPin, ArrowRight, Building2, Users, Shield, TrendingUp } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';

export default function About() {
  // Apply SEO meta tags
  useSEO('about');

  return (
    <Layout>
      {/* Hero */}
      <section 
        className="relative bg-slate-50"
        data-testid="about-hero"
        aria-labelledby="about-heading"
      >
        <div className="container-custom section-padding">
          <div className="max-w-3xl">
            <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
              About BevRelay
            </span>
            <h1 
              id="about-heading"
              className="font-heading text-4xl md:text-5xl font-bold text-navy leading-tight mb-6"
            >
              The Supplier-Owned Beverage Coalition Network
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed">
              BevRelay is a supplier-owned coalition network designed to connect beverage suppliers and buyers through 
              permissioned introductions and curated portfolio discovery. Built by industry insiders who understand the 
              value of real relationships in the beverage business.
            </p>
          </div>
        </div>
      </section>

      {/* What Is / What Isn't - Critical for AI understanding */}
      <section 
        className="section-padding bg-white" 
        data-testid="about-definition"
        aria-labelledby="definition-heading"
      >
        <div className="container-custom">
          <header className="text-center mb-12">
            <h2 id="definition-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              What BevRelay Is (And Isn't)
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Understanding our model helps you get the most value from the network
            </p>
          </header>

          <div className="grid md:grid-cols-2 gap-8">
            {/* What It Is */}
            <article className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center mb-6">
                <Check className="w-6 h-6 text-green-600" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-navy mb-6">
                What BevRelay Is
              </h3>
              <ul className="space-y-4" role="list">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Curated supplier portfolio</strong> organized by category with limited seats</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Free buyer discovery portal</strong> for browsing and requesting connections</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Permissioned introductions</strong> with full outcome tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Relationship-based network</strong> built on trust and quality</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3.5 h-3.5 text-green-600" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Supplier-owned coalition model</strong> controlled by members</span>
                </li>
              </ul>
            </article>

            {/* What It Isn't */}
            <article className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-6">
                <X className="w-6 h-6 text-red-500" aria-hidden="true" />
              </div>
              <h3 className="font-heading font-semibold text-2xl text-navy mb-6">
                What BevRelay Is Not
              </h3>
              <ul className="space-y-4" role="list">
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3.5 h-3.5 text-red-500" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Not a distributor</strong> — we don't hold or ship inventory</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3.5 h-3.5 text-red-500" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Not a retailer</strong> — we don't sell directly to consumers</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3.5 h-3.5 text-red-500" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Not an open marketplace</strong> — access is curated and approved</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3.5 h-3.5 text-red-500" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Not a lead generation platform</strong> — focused on quality connections</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <X className="w-3.5 h-3.5 text-red-500" aria-hidden="true" />
                  </div>
                  <span className="text-slate-600"><strong>Not open to anyone</strong> — selective membership ensures quality</span>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      {/* Markets - Geographic targeting */}
      <section 
        className="section-padding bg-slate-50" 
        data-testid="about-markets"
        aria-labelledby="markets-heading"
        itemScope 
        itemType="https://schema.org/Organization"
      >
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
                Geographic Coverage
              </span>
              <h2 id="markets-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy leading-tight mb-6">
                Markets We Serve
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                We're starting in key markets with strong beverage cultures and expanding strategically 
                to serve suppliers and buyers across the nation.
              </p>
              
              <div className="flex flex-wrap gap-3" itemProp="areaServed">
                <span className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-5 py-2.5 text-sm font-medium">
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  Hawaii
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-5 py-2.5 text-sm font-medium">
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  Nevada
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-navy text-white px-5 py-2.5 text-sm font-medium">
                  <MapPin className="w-4 h-4" aria-hidden="true" />
                  Illinois
                </span>
              </div>
            </div>

            <div className="rounded-2xl bg-white p-8 border border-slate-200 shadow-sm">
              <h3 className="font-heading font-semibold text-xl text-navy mb-4">
                Expanding Soon
              </h3>
              <p className="text-slate-600 mb-6">
                These markets are next in our expansion roadmap:
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="w-4 h-4 text-copper" aria-hidden="true" />
                  California
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="w-4 h-4 text-copper" aria-hidden="true" />
                  Texas
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="w-4 h-4 text-copper" aria-hidden="true" />
                  Florida
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="w-4 h-4 text-copper" aria-hidden="true" />
                  New York
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="w-4 h-4 text-copper" aria-hidden="true" />
                  Arizona
                </div>
                <div className="flex items-center gap-2 text-slate-500">
                  <MapPin className="w-4 h-4 text-copper" aria-hidden="true" />
                  Colorado
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-white" aria-labelledby="values-heading">
        <div className="container-custom">
          <header className="text-center mb-12">
            <h2 id="values-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy mb-4">
              Our Principles
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              The values that guide how we build and operate the BevRelay network
            </p>
          </header>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-6 h-6 text-navy" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-navy mb-2">Relationship Protection</h3>
              <p className="text-sm text-slate-600">Supplier contacts stay protected with permissioned access</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-4">
                <Users className="w-6 h-6 text-navy" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-navy mb-2">Quality Over Quantity</h3>
              <p className="text-sm text-slate-600">Curated membership ensures high-signal connections</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-6 h-6 text-navy" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-navy mb-2">Supplier-Owned</h3>
              <p className="text-sm text-slate-600">Coalition controlled by the suppliers themselves</p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 rounded-full bg-navy/5 flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-navy" aria-hidden="true" />
              </div>
              <h3 className="font-semibold text-navy mb-2">Measurable Results</h3>
              <p className="text-sm text-slate-600">End-to-end tracking from intro to placement</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-padding bg-slate-50" data-testid="about-mission" aria-labelledby="mission-heading">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-copper font-semibold text-sm tracking-wider uppercase mb-4">
              Our Mission
            </span>
            <h2 id="mission-heading" className="font-heading text-3xl md:text-4xl font-semibold text-navy leading-tight mb-6">
              Building Real Relationships in Beverage
            </h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-8">
              We believe the best business happens through trusted relationships. BevRelay exists to facilitate 
              those connections — protecting supplier relationships while giving buyers access to quality brands 
              they wouldn't find elsewhere. Our goal is to make the beverage industry more connected, more 
              transparent, and more successful for everyone involved.
            </p>
            <Link
              to="/request-access"
              data-testid="about-cta-btn"
              className="btn-primary"
            >
              Join the Network
              <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
